import React, { memo, useCallback, useState } from "react";
import { View, Pressable } from "react-native";
import FaqItem from "./faq-item";
import { v4 as uuidv4 } from "uuid";
import { LOCAL_ID_PREFIX, MAX_FAQS } from "./faq-constants";
import { usePostHog } from "posthog-js/react";
import SettingsItem from "../common/settings-item";
import { useSettingsData } from "../../../../contexts/SettingsDataContext";
import StyledText from "../../../common/ui/styled-text";
import { ChevronDown, ChevronUp, HelpCircle, Plus } from "lucide-react-native";

const Faq = () => {
  const posthog = usePostHog();
  const { settings, handleFaq, isTestCallActive, validationErrors } =
    useSettingsData();

  const faqs = settings.faqs;

  const [expandedItems, setExpandedItems] = useState(() =>
    faqs.map((faq) => faq.question === "" && faq.answer === "")
  );

  const allCollapsed = useCallback(
    () => expandedItems.every((item) => item === false),
    [expandedItems]
  );

  const toggleAll = useCallback(() => {
    const newExpandedState = allCollapsed();
    posthog.capture("faq_toggled_all", {
      toggled_to: newExpandedState,
    });
    setExpandedItems(new Array(faqs.length).fill(newExpandedState));
  }, [allCollapsed, faqs.length]);

  const setItem = (index: number, value: boolean) => {
    setExpandedItems((prev) => {
      const newState = [...prev];
      newState[index] = value;
      return newState;
    });
  };

  const hasReachedMaxFaqs = faqs.length >= MAX_FAQS;
  const aboutToReachMaxFaqs = faqs.length >= MAX_FAQS - 1;

  const handleAddFaq = useCallback(() => {
    if (aboutToReachMaxFaqs) {
      posthog.capture("faq_limit_reached_warning");
    }
    if (hasReachedMaxFaqs) {
      return;
    }
    posthog.capture("faq_added");
    handleFaq([
      ...faqs,
      { id: LOCAL_ID_PREFIX + uuidv4(), question: "", answer: "" },
    ]);
    setExpandedItems((prev) => [...prev, true]);
  }, [aboutToReachMaxFaqs, hasReachedMaxFaqs, handleFaq, faqs]);

  const toggleAllButton = (
    <Pressable
      onPress={toggleAll}
      className="cursor-pointer flex-shrink-0 border border-gray-200 rounded-md px-2 py-1"
      disabled={isTestCallActive}
    >
      <View className="flex-row items-center space-x-1">
        <StyledText className="text-gray-500 text-xs">
          {allCollapsed() ? "Expand all" : "Collapse all"}
        </StyledText>
        {allCollapsed() ? (
          <ChevronDown size={16} color="#6b7280" />
        ) : (
          <ChevronUp size={16} color="#6b7280" />
        )}
      </View>
    </Pressable>
  );

  return (
    <SettingsItem
      title="Frequently Asked Questions"
      description="Answer common questions your customers might be asking. Please note that links are not supported."
      icon={HelpCircle}
      leftItem={faqs.length > 0 ? toggleAllButton : null}
    >
      <View className="flex-1 w-full">
        {validationErrors.faqs && (
          <StyledText className="text-red-500 text-sm font-semibold mb-3 mt-[-10px]">
            {validationErrors.faqs}
          </StyledText>
        )}
        {faqs.map((item, index) => (
          <FaqItem
            key={item.id}
            isExpanded={expandedItems[index]}
            setIsExpanded={(value) => setItem(index, value)}
            faq={item}
            setFaq={(faq) =>
              handleFaq(faqs.map((f) => (f.id === faq.id ? faq : f)))
            }
            onDelete={() => {
              handleFaq(faqs.filter((faq) => faq.id !== item.id));
              setExpandedItems(expandedItems.filter((_, i) => i !== index));
            }}
            isNew={item.question === "" && item.answer === ""}
            disabled={isTestCallActive}
          />
        ))}

        <Pressable
          onPress={handleAddFaq}
          className={`mb-2 flex-row items-center justify-center px-3 py-2 rounded-md bg-gray-50 hover:bg-gray-100 border border-gray-700 ${
            hasReachedMaxFaqs
              ? "opacity-50 cursor-not-allowed"
              : "cursor-pointer"
          }`}
          disabled={hasReachedMaxFaqs || isTestCallActive}
        >
          <Plus
            size={16}
            color={hasReachedMaxFaqs ? "#9CA3AF" : "#111827"}
            className="mr-1"
          />
          <StyledText className="text-base font-semibold text-gray-900">
            {hasReachedMaxFaqs ? "FAQ Limit Reached" : "Add FAQ"}
          </StyledText>
        </Pressable>
        {hasReachedMaxFaqs && (
          <StyledText className="text-sm text-gray-500 text-center mb-3">
            You can have a maximum of {MAX_FAQS} FAQs
          </StyledText>
        )}
      </View>
    </SettingsItem>
  );
};

export default memo(Faq);
